import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { zipSync, unzipSync, strToU8, strFromU8 } from "fflate";
import { igolfMappingRequest } from "API/apiConfig";
import {
  CourseElevationDataDetails,
  CourseElevationStepDataDetails,
  CourseElevationDataThreeMeterDetails,
  CourseGPSElementsDetails,
  CourseElevationDataCreate,
  CourseGPSVectorDetails,
} from "API/apiRequest";
import axios from "axios";

const initialState = {
  heatMapDetails:{},
  editableHeatArea:'',
  selectedElevationValue:-1,
  course_elevation_data_details:{},
  course_elevation_data_details_status:"idle",
  course_gps_vector_details_status:"idle",
  isLocalUpdating:false
};

// export const driving_range_gps_create = createAsyncThunk(
//   "/drivingRangeGPSCreate",
//   async ({ body, user }) => {
//     let res = await igolfMappingRequest(DrivingRangeGPSCreate, body, user)
//       .then((res) => res)
//       .catch((error) => error);
//     let resData = { data: res?.data };
//     return resData;
//   }
// );

// export const driving_range_gps_update = createAsyncThunk(
//   "/drivingRangeGPSUpdate",
//   async ({ body, user }) => {
//     let res = await igolfMappingRequest(DrivingRangeGPSUpdate, body, user)
//       .then((res) => res)
//       .catch((error) => error);
//     let resData = { data: res?.data };
//     return resData;
//   }
// );
// export const driving_range_gps_details = createAsyncThunk(
//   "/drivingRangeGPSDetails",
//   async ({ body, user }) => {
//     let res = await igolfMappingRequest(DrivingRangeGPSDetails, body, user)
//       .then((res) => res)
//       .catch((error) => error);
//     let resData = { data: res?.data };
//     return resData;
//   }
// );

export const course_gps_vector_details = createAsyncThunk(
  "/CourseGPSVectorDetails",
  async ({ body, user }) => {
    let res = await igolfMappingRequest(CourseGPSVectorDetails, body, user)
      .then((res) => res)
      .catch((error) => error);
      let resData = { data: res?.data };
    return resData;
  }
);

export const course_elevation_data_details = createAsyncThunk(
  "/CourseElevationDataDetails",
  async ({ body, user }) => {
    let res = await igolfMappingRequest(CourseElevationDataDetails, body, user)
      .then((res) => res)
      .catch((error) => error);
      let resData = { data: res?.data };
    return resData;
  }
);
export const course_json_data = createAsyncThunk(
  "/getJSON",
  async (jsonUrl) => {
  let result =await  axios.get(jsonUrl)
    .then(response => response.data)
    .catch(error => {
      console.error('Error fetching JSON:', error);
    });

    return result
  }
);

// ---- New HeatMap (Three.js terrain editor) data loaders ---------------------
// Single source of truth for the resolution -> fetch endpoint mapping. The 1m
// endpoint takes a `step` param; 3m and 10m are dedicated actions (no step).
// (Confirmed from Josh's api_client.py + the iGolf OpenAPI request bodies.)
const RESOLUTION_ACTION = {
  1: CourseElevationStepDataDetails,
  3: CourseElevationDataThreeMeterDetails,
  10: CourseElevationDataDetails,
};

// Terrain/elements responses wrap the payload in a Status/ErrorMessage envelope
// and deliver it via an S3 link: `jsonFullUrl` (raw .json) or `jsonUrl` (.zip).
// We prefer jsonFullUrl so no client-side unzip is needed here (zip handling is
// only required for the upload path). Returns the parsed JSON, or the inline
// payload when the API embeds it directly.
const followPayload = async (data, inlineKey) => {
  if (data?.[inlineKey]) return data; // payload embedded inline
  // Prefer the raw .json link; fall back to the zipped one and unzip it.
  const url = data?.jsonFullUrl || data?.jsonUrl;
  if (!url) throw new Error("No payload URL in response");
  const isZip = url.split("?")[0].toLowerCase().endsWith(".zip");
  if (isZip) {
    const res = await axios.get(url, { responseType: "arraybuffer" });
    const files = unzipSync(new Uint8Array(res.data));
    const name =
      Object.keys(files).find((n) => n.toLowerCase().endsWith(".json")) ||
      Object.keys(files)[0];
    if (!name) throw new Error("Zip payload is empty");
    return JSON.parse(strFromU8(files[name]));
  }
  const fileRes = await axios.get(url);
  return fileRes?.data;
};

const envelopeFailed = (data, inlineKey) =>
  data?.Status !== undefined && Number(data.Status) !== 1 && !data?.[inlineKey];

// Load terrain for a course at a given resolution (1 | 3 | 10).
export const load_course_terrain = createAsyncThunk(
  "/loadCourseTerrain",
  async ({ id_course, step, user }) => {
    const res = Number(step) || 1;
    const action = RESOLUTION_ACTION[res] || CourseElevationStepDataDetails;
    const body = { id_course };
    if (action === CourseElevationStepDataDetails) body.step = res;
    const apiRes = await igolfMappingRequest(action, body, user);
    const data = apiRes?.data;
    if (envelopeFailed(data, "elevationArray")) {
      throw new Error(data?.ErrorMessage || `Status ${data?.Status}`);
    }
    const terrain = await followPayload(data, "elevationArray");
    return { terrain, dateModified: data?.dateModified || null };
  }
);

// Load course element vectors (greens, fairways, bunkers, ...). New endpoint,
// superset of CourseGPSVectorDetails; same `vectorGPSObject` shape.
export const load_course_elements = createAsyncThunk(
  "/loadCourseElements",
  async ({ id_course, user }) => {
    const apiRes = await igolfMappingRequest(CourseGPSElementsDetails, { id_course }, user);
    const data = apiRes?.data;
    if (envelopeFailed(data, "vectorGPSObject")) {
      throw new Error(data?.ErrorMessage || `Status ${data?.Status}`);
    }
    const elements = await followPayload(data, "vectorGPSObject");
    return { elements, dateModified: data?.dateModified || null };
  }
);

// Upload edited terrain via the pre-signed S3 two-step flow:
//  1. GET_UPLOAD_URLS -> { jsonUrl (presigned .zip), jsonFullUrl (presigned .json) }
//  2. PUT the raw JSON to jsonFullUrl, and a zip (<id>.json inside) to jsonUrl
//     -- these go DIRECT to S3, with NO iGolf signing (the URL is pre-signed).
//  3. COMPLETE_UPLOAD.
// S3 file id is the 7-digit zero-padded id_courseInt (matches the download keys).
const pad7 = (n) => String(n).padStart(7, "0");

export const upload_course_elevation = createAsyncThunk(
  "/uploadCourseElevation",
  async ({ id_courseInt, step, jsonText, user }) => {
    const idInt = Number(id_courseInt);
    const stepNum = Number(step);

    // 1) get pre-signed URLs
    const getRes = await igolfMappingRequest(
      CourseElevationDataCreate,
      { id_courseInt: idInt, step: stepNum, operation: "GET_UPLOAD_URLS" },
      user
    );
    const urls = getRes?.data;
    if (Number(urls?.Status) !== 1) {
      throw new Error(urls?.ErrorMessage || "Could not get upload URLs");
    }
    const { jsonUrl, jsonFullUrl } = urls;
    if (!jsonUrl || !jsonFullUrl) {
      throw new Error("Upload URLs missing from response");
    }

    // 2) PUT both files to S3 in parallel (independent uploads).
    // IMPORTANT: the SigV2 pre-signed PUT URLs are signed WITHOUT a Content-Type,
    // and SigV2 includes Content-Type in the signature. So we must NOT send a
    // Content-Type header — otherwise S3 returns 403 SignatureDoesNotMatch.
    // Sending the bodies as raw bytes (Uint8Array) makes the browser omit the
    // Content-Type header (a string body would auto-add text/plain). The archive
    // contains "<paddedId>.json" to match the download/JSON-FULL key naming.
    const zipped = zipSync({ [`${pad7(idInt)}.json`]: strToU8(jsonText) });
    const [fullPut, zipPut] = await Promise.all([
      fetch(jsonFullUrl, { method: "PUT", body: strToU8(jsonText) }),
      fetch(jsonUrl, { method: "PUT", body: zipped }),
    ]);
    if (!fullPut.ok) throw new Error(`Full JSON upload failed (HTTP ${fullPut.status})`);
    if (!zipPut.ok) throw new Error(`Zip upload failed (HTTP ${zipPut.status})`);

    // 3) confirm
    const confirmRes = await igolfMappingRequest(
      CourseElevationDataCreate,
      { id_courseInt: idInt, step: stepNum, operation: "COMPLETE_UPLOAD" },
      user
    );
    if (Number(confirmRes?.data?.Status) !== 1) {
      throw new Error(confirmRes?.data?.ErrorMessage || "Confirm upload failed");
    }
    return { ok: true };
  }
);

export const HeatMapSlice = createSlice({
  name: "HeatMapSlice",
  initialState,
  reducers: {
    setHeatMapData: (state, action) => {
        state.heatMapDetails=action?.payload;
      },
      setEditableHeatArea: (state, action) => {
        state.editableHeatArea=action?.payload;
      },
      setSelectedElevationValue: (state, action) => {
        state.selectedElevationValue=action?.payload;
      },
      setSelectedHeatMapRow:(state, action) => {
        state.selectedHeatMapRow=action?.payload;
      },
      setIsLocalUpdating:(state, action) => {
        state.isLocalUpdating=action?.payload;
      },
      resetHeatMapData:(state) => {
        state.heatMapDetails={};
        state.editableHeatArea='';
        state.selectedElevationValue=-1;
        state.course_elevation_data_details={};
        state.course_elevation_data_details_status="idle";
        state.course_gps_vector_details_status="idle";
        state.isLocalUpdating=false;
      },
  },
extraReducers:(builder)=>{
  builder
  .addCase(course_json_data.pending, (state) => {
    state.course_elevation_data_details_status = "loading";
  })
  .addCase(course_json_data.fulfilled, (state, { payload }) => {
    state.course_elevation_data_details_status = "idle";
    state.course_elevation_data_details=payload
  })
  .addCase(course_json_data.rejected, (state) => {
    state.course_elevation_data_details_status = "idle";
  })
  //vector details
  .addCase(course_gps_vector_details.pending, (state) => {
    state.course_gps_vector_details_status = "loading";
  })
  .addCase(course_gps_vector_details.fulfilled, (state, { payload }) => {
    state.course_gps_vector_details_status = "idle";
    state.heatMapDetails=payload?.data
  })
  .addCase(course_gps_vector_details.rejected, (state) => {
    state.course_gps_vector_details_status = "idle";
  })

}
//   extraReducers: (builder) => {
//     builder
//       // map data submit
//       .addCase(driving_range_gps_create.pending, (state) => {
//         state.driving_range_gps_create_status = "loading";
//       })
//       .addCase(driving_range_gps_create.fulfilled, (state, { payload }) => {
//         state.driving_range_gps_create_status = "idle";
//         // // state.profile_information_data =  payload?.data;
//         // // if(payload?.data !== undefined){
//         // //   localStorage.setItem("profile_data", JSON.stringify(payload?.data));
//         // // }
//       })
//       .addCase(driving_range_gps_create.rejected, (state) => {
//         state.driving_range_gps_create_status = "idle";
//       })
//       // map data fetch
//       .addCase(driving_range_gps_details.pending, (state) => {
//         state.driving_range_gps_details_status = "loading";
//       })
//       .addCase(driving_range_gps_details.fulfilled, (state, { payload }) => {
//         state.driving_range_gps_details_status = "idle";
//         // redux is filled from the component
//       })
//       .addCase(driving_range_gps_details.rejected, (state) => {
//         state.driving_range_gps_details_status = "idle";
//       })
//       .addCase(driving_range_gps_update.pending, (state) => {
//         state.driving_range_gps_update_status = "loading";
//       })
//       .addCase(driving_range_gps_update.fulfilled, (state, { payload }) => {
//         state.driving_range_gps_update_status = "idle";
//       })
//       .addCase(driving_range_gps_update.rejected, (state) => {
//         state.driving_range_gps_update_status = "idle";
//       })

      
//   },
});

export const {
    setHeatMapData,
    setEditableHeatArea,
    setSelectedElevationValue,
    setSelectedHeatMapRow,
    setIsLocalUpdating,
    resetHeatMapData
} = HeatMapSlice.actions;

export default HeatMapSlice.reducer;
